import type {
  BabyGoal,
  CarrierType,
  EggSource,
  GuidanceReport,
  JourneyLocale,
  RecommendedPathway,
  SpermSource,
  UserCategory
} from './types';

type Step = 'intro' | 'goal' | 'category' | 'paths' | 'review' | 'medical' | 'report';
type ReportTab = 'legality' | 'pathway' | 'medical' | 'cost' | 'timeline' | 'risk' | 'register';

export function getJourneyLocale(): JourneyLocale {
  const host = window.location.hostname.toLowerCase();
  const path = window.location.pathname.toLowerCase();
  if (host.startsWith('vn.') || host === 'www.vn.ivftoday.org' || path === '/vi' || path.startsWith('/vi/')) return 'vi';
  if (host.startsWith('en.') || path === '/en' || path.startsWith('/en/')) return 'en';
  return 'zh-Hans';
}

export function getLeadSource(locale: JourneyLocale) {
  const sourceLocale = locale === 'vi' ? 'vi' : locale === 'en' ? 'en' : 'zh-Hans';
  return {
    sourceLocale,
    sourceHost: window.location.hostname,
    sourceChannel: locale === 'vi' ? 'regional-vietnam' : locale === 'en' ? 'regional-english' : 'main-site'
  };
}

export const localeMeta: Record<JourneyLocale, { lang: string; ogLocale: string; canonical: string; title: string; description: string; imageAlt: string }> = {
  'zh-Hans': {
    lang: 'zh-Hans',
    ogLocale: 'zh_CN',
    canonical: 'https://ivftoday.org/',
    title: 'IVFToday | 男同性伴侣与单身男性父亲路径规划',
    description: '为男同性伴侣与单身男性整理供卵、胚胎、孕母、跨国亲权、费用与时间线的私密初步规划。',
    imageAlt: '两位准爸爸与宝宝在现代居家空间中的父亲路径视觉'
  },
  en: {
    lang: 'en',
    ogLocale: 'en_US',
    canonical: 'https://en.ivftoday.org/',
    title: 'IVFToday | Private Fatherhood Planning for Gay Men and Single Fathers',
    description: 'A private step-by-step donor egg, embryo, gestational carrier, legal access, cost, and timeline planner for gay men and single intended fathers.',
    imageAlt: 'Two intended fathers with their baby in a modern home'
  },
  vi: {
    lang: 'vi',
    ogLocale: 'vi_VN',
    canonical: 'https://vn.ivftoday.org/',
    title: 'IVFToday Việt Nam | Lập kế hoạch làm cha cho cặp đôi nam và nam độc thân',
    description: 'Công cụ riêng tư cho trứng hiến, phôi, người mang thai hộ, pháp lý, chi phí và lịch trình dành cho cặp đôi nam và nam độc thân.',
    imageAlt: 'Hai người cha dự định cùng em bé trong không gian sống hiện đại'
  }
};

export function getLocaleMeta(locale: JourneyLocale) {
  const meta = localeMeta[locale];
  if (typeof window !== 'undefined' && window.location.hostname.toLowerCase() === 'lgbtq.ivftoday.org') {
    return { ...meta, canonical: 'https://lgbtq.ivftoday.org/' };
  }
  return meta;
}

export const stepTitlesByLocale: Record<JourneyLocale, Record<Step, string>> = {
  'zh-Hans': {
    intro: '引言',
    goal: '家庭愿景',
    category: '身份类型',
    paths: '求子途径',
    review: '可行性检查',
    medical: '生理指标',
    report: '规划报告'
  },
  en: {
    intro: 'Introduction',
    goal: 'Family Vision',
    category: 'Identity',
    paths: 'Resources',
    review: 'Feasibility Review',
    medical: 'Medical Inputs',
    report: 'Planning Report'
  },
  vi: {
    intro: 'Giới thiệu',
    goal: 'Mục tiêu gia đình',
    category: 'Nhóm khách hàng',
    paths: 'Nguồn sinh sản',
    review: 'Kiểm tra khả thi',
    medical: 'Chỉ số y khoa',
    report: 'Báo cáo sơ bộ'
  }
};

export const babyGoalLabelsByLocale: Record<JourneyLocale, Record<BabyGoal, { title: string; en: string; copy: string }>> = {
  'zh-Hans': {
    healthy_singleton: { title: '一个健康宝宝，性别无偏好', en: 'Healthy Singleton, No Sex Preference', copy: '以单胎安全为优先，先评估临床、法律与预算可行性。' },
    male_singleton: { title: '希望一个男宝宝', en: 'One Son Preference', copy: '记录性别愿景，但必须通过目的地法律与胚胎筛查规范。' },
    female_singleton: { title: '希望一个女宝宝', en: 'One Daughter Preference', copy: '记录性别愿景，但不直接承诺可行性或胚胎结果。' },
    twins_boy_girl: { title: '希望龙凤胎', en: 'Boy-Girl Twins Preference', copy: '双胎愿景需医师先审核多胎妊娠风险与单胚胎替代方案。' },
    twins_boy_boy: { title: '希望两个男宝', en: 'Two Sons Preference', copy: '同时涉及性别与双胎安全审核，只能生成初步评估。' },
    twins_girl_girl: { title: '希望两个女宝', en: 'Two Daughters Preference', copy: '同时涉及性别与双胎安全审核，只能生成初步评估。' },
    existing_embryo_transfer: { title: '已有胚胎，希望安排移植', en: 'Existing Embryo Transfer', copy: '以胚胎数量、PGT 状态、冷冻地点与转运文件作为核心。' },
    unknown: { title: '还不确定，希望先评估', en: 'Not Sure, Preliminary Assessment', copy: '先厘清身份、配子来源、承载方式与合规国家。' }
  },
  en: {
    healthy_singleton: { title: 'One healthy baby, no sex preference', en: 'Healthy Singleton, No Sex Preference', copy: 'Prioritize singleton safety, then review clinical, legal, and budget feasibility.' },
    male_singleton: { title: 'Hope for one son', en: 'One Son Preference', copy: 'Records the family vision, but it must pass destination law and embryo-screening rules.' },
    female_singleton: { title: 'Hope for one daughter', en: 'One Daughter Preference', copy: 'Records the family vision without promising feasibility or embryo results.' },
    twins_boy_girl: { title: 'Hope for boy-girl twins', en: 'Boy-Girl Twins Preference', copy: 'Twin goals require medical review of multiple pregnancy risk and single embryo alternatives.' },
    twins_boy_boy: { title: 'Hope for two sons', en: 'Two Sons Preference', copy: 'Combines sex preference and twin risk review, so the report remains preliminary.' },
    twins_girl_girl: { title: 'Hope for two daughters', en: 'Two Daughters Preference', copy: 'Combines sex preference and twin risk review, so the report remains preliminary.' },
    existing_embryo_transfer: { title: 'Existing embryos, arrange transfer', en: 'Existing Embryo Transfer', copy: 'Centers on embryo count, PGT status, frozen location, and transport documents.' },
    unknown: { title: 'Not sure yet, assess first', en: 'Not Sure, Preliminary Assessment', copy: 'Clarify identity, gamete sources, carrier, and compliant countries first.' }
  },
  vi: {
    healthy_singleton: { title: 'Một em bé khỏe mạnh, không chọn giới tính', en: 'Healthy Singleton, No Sex Preference', copy: 'Ưu tiên an toàn đơn thai, sau đó đánh giá y khoa, pháp lý và ngân sách.' },
    male_singleton: { title: 'Mong muốn một bé trai', en: 'One Son Preference', copy: 'Ghi nhận mong muốn gia đình, nhưng phải qua luật điểm đến và quy định sàng lọc phôi.' },
    female_singleton: { title: 'Mong muốn một bé gái', en: 'One Daughter Preference', copy: 'Ghi nhận mong muốn mà không cam kết khả thi hoặc kết quả phôi.' },
    twins_boy_girl: { title: 'Mong muốn sinh đôi trai gái', en: 'Boy-Girl Twins Preference', copy: 'Mục tiêu song thai cần bác sĩ đánh giá rủi ro đa thai và phương án chuyển một phôi.' },
    twins_boy_boy: { title: 'Mong muốn hai bé trai', en: 'Two Sons Preference', copy: 'Vừa liên quan giới tính vừa liên quan song thai, nên báo cáo chỉ là đánh giá sơ bộ.' },
    twins_girl_girl: { title: 'Mong muốn hai bé gái', en: 'Two Daughters Preference', copy: 'Vừa liên quan giới tính vừa liên quan song thai, nên báo cáo chỉ là đánh giá sơ bộ.' },
    existing_embryo_transfer: { title: 'Đã có phôi, muốn sắp xếp chuyển phôi', en: 'Existing Embryo Transfer', copy: 'Tập trung vào số lượng phôi, PGT, nơi đông lạnh và giấy tờ vận chuyển.' },
    unknown: { title: 'Chưa chắc, muốn đánh giá trước', en: 'Not Sure, Preliminary Assessment', copy: 'Làm rõ thân phận, nguồn giao tử, người mang thai và quốc gia phù hợp trước.' }
  }
};

export const categoryLabelsByLocale: Record<JourneyLocale, Record<UserCategory, { title: string; en: string; copy: string }>> = {
  'zh-Hans': {
    spouses: { title: '配偶与异性伴侣', en: 'Married Couples & Spouses', copy: '寻求常规试管婴儿（IVF）周期、胚胎植入前基因检测（PGT），或妊娠代孕途径的合法伴侣。' },
    single_woman: { title: '单身女性传承者', en: 'Single Intending Mother', copy: '独立女性探索低温捐精授精、冷冻卵子复活取卵、亲自怀孕，或代孕助孕途径。' },
    single_man: { title: '单身准爸爸', en: 'Single Intended Father', copy: '以个人精子、第三方供卵、胚胎、孕母承载与单身父亲亲权为核心，规划清晰的跨国路径。' },
    female_couple: { title: '双女伴侣', en: 'Female Couple / ROPA Path', copy: '女性伴侣探索双母试管（ROPA）、捐精、伴侣怀孕或亲自怀孕的合规家庭路径。' },
    male_couple: { title: '男同性伴侣', en: 'Gay Male Couple', copy: '共同规划双方精子使用、供卵、胚胎、孕母承载，以及两位父亲的出生亲权文件。' },
    other_lgbtq: { title: '其他 / 跨性别 / 未分类', en: 'Other LGBTQ+ / Individual Review', copy: '跨性别、非二元或未分类家庭情境，需由顾问先厘清配子、承载与法律身份。' }
  },
  en: {
    spouses: { title: 'Couple / opposite-sex partners', en: 'Married Couples & Spouses', copy: 'Legal partners exploring IVF, PGT, self-gestation, or secure surrogacy pathways.' },
    single_woman: { title: 'Single intending mother', en: 'Single Intending Mother', copy: 'Independent women exploring donor sperm IUI/IVF, egg freezing, self-gestation, or surrogacy support.' },
    single_man: { title: 'Single intending father', en: 'Single Intending Father', copy: 'Independent men planning donor egg and gestational carrier arrangements under a secure legal path.' },
    female_couple: { title: 'Female couple', en: 'Female Couple / ROPA Path', copy: 'Female partners reviewing ROPA, donor sperm, partner pregnancy, or self-gestation access.' },
    male_couple: { title: 'Male couple', en: 'Male Couple / Surrogacy Path', copy: 'Male partners usually need donor eggs, sperm-source planning, surrogacy, and parentage law review.' },
    other_lgbtq: { title: 'Other / transgender / not categorized', en: 'Other LGBTQ+ / Individual Review', copy: 'Transgender, non-binary, or uncategorized situations require advisor scoping of gametes, carrier, and legal identity.' }
  },
  vi: {
    spouses: { title: 'Vợ chồng / cặp đôi khác giới', en: 'Married Couples & Spouses', copy: 'Cặp đôi hợp pháp đang xem xét IVF, PGT, tự mang thai hoặc lộ trình mang thai hộ an toàn.' },
    single_woman: { title: 'Phụ nữ độc thân muốn làm mẹ', en: 'Single Intending Mother', copy: 'Phụ nữ độc lập xem xét IUI/IVF với tinh trùng hiến tặng, trữ trứng, tự mang thai hoặc hỗ trợ mang thai hộ.' },
    single_man: { title: 'Nam độc thân muốn làm cha', en: 'Single Intending Father', copy: 'Nam độc thân cần lộ trình hiến trứng, phôi và người mang thai hộ trong khung pháp lý an toàn.' },
    female_couple: { title: 'Cặp đôi nữ', en: 'Female Couple / ROPA Path', copy: 'Cặp đôi nữ xem xét ROPA, tinh trùng hiến tặng, một người cung cấp trứng và/hoặc một người mang thai.' },
    male_couple: { title: 'Cặp đôi nam', en: 'Male Couple / Surrogacy Path', copy: 'Cặp đôi nam thường cần hiến trứng, xác nhận nguồn tinh trùng, mang thai hộ và pháp lý cha mẹ.' },
    other_lgbtq: { title: 'Khác / chuyển giới / chưa phân loại', en: 'Other LGBTQ+ / Individual Review', copy: 'Tình huống chuyển giới, phi nhị nguyên hoặc chưa phân loại cần cố vấn làm rõ giao tử, người mang thai và thân phận pháp lý.' }
  }
};

export const pathwayLabelsByLocale: Record<JourneyLocale, Record<RecommendedPathway, { title: string; en: string; copy: string }>> = {
  'zh-Hans': {
    iui_or_ivf: { title: 'IUI / IVF 初阶至进阶路径', en: 'IUI or IVF Candidate', copy: '配子与承载条件相对完整，可由医师根据年龄、输卵管、精液与时间成本决定 IUI 或 IVF。' },
    ivf: { title: '常规 IVF 试管婴儿路径', en: 'Classical IVF', copy: '以促排取卵、体外受精、胚胎培养与移植为主轴，适合需要较高可控性的家庭。' },
    icsi_ivf: { title: 'ICSI / IVF 精准受精路径', en: 'ICSI IVF', copy: '当精子条件待确认或偏弱时，优先由胚胎实验室评估单精子注射与精液优化。' },
    ropa: { title: '双母试管 ROPA 路径', en: 'Reciprocal IVF / ROPA', copy: '一方提供卵子，另一方承载妊娠，通常需结合捐精与所在地多元家庭法律核对。' },
    donor_egg_ivf: { title: '供卵 IVF 路径', en: 'Donor Egg IVF', copy: '以第三方卵子建立胚胎，重点在供卵者筛查、配型、法律文件与胚胎实验室品质。' },
    embryo_transfer: { title: '既有胚胎移植 / 转运路径', en: 'Embryo Transfer / Transport', copy: '以既有冷冻胚胎为起点，核心在胚胎资料、PGT 状态、冷冻地点与跨境转运可行性。' },
    surrogacy_ivf: { title: '代孕 IVF 亲权路径', en: 'Surrogacy IVF', copy: '需要供卵或自卵、自精或捐精、孕母匹配、托管信托、法律公证与出生亲权确立。' },
    needs_review: { title: '需顾问个案评估', en: 'Consultant Review Required', copy: '目前资源、承载或身份设定存在不确定性，建议先由顾问厘清再形成确定方案。' }
  },
  en: {
    iui_or_ivf: { title: 'IUI / IVF early-to-advanced path', en: 'IUI or IVF Candidate', copy: 'Gamete and carrier assumptions are mostly complete; a physician can decide between IUI and IVF.' },
    ivf: { title: 'Classical IVF pathway', en: 'Classical IVF', copy: 'Ovarian stimulation, retrieval, fertilization, embryo culture, and transfer for families needing higher control.' },
    icsi_ivf: { title: 'ICSI / IVF precision fertilization', en: 'ICSI IVF', copy: 'When sperm factors are weak or uncertain, the embryology lab should review ICSI and sperm optimization.' },
    ropa: { title: 'ROPA reciprocal IVF pathway', en: 'Reciprocal IVF / ROPA', copy: 'One partner provides eggs and the other carries pregnancy, with donor sperm and local family-law review.' },
    donor_egg_ivf: { title: 'Donor egg IVF pathway', en: 'Donor Egg IVF', copy: 'Creates embryos with third-party eggs; screening, matching, consent, and lab quality become central.' },
    embryo_transfer: { title: 'Existing embryo transfer / transport', en: 'Embryo Transfer / Transport', copy: 'Starts from frozen embryos; focus on embryo records, PGT, storage location, and transport feasibility.' },
    surrogacy_ivf: { title: 'Surrogacy IVF parentage pathway', en: 'Surrogacy IVF', copy: 'Requires gamete planning, carrier match, escrow, legal notarization, and parentage confirmation.' },
    needs_review: { title: 'Advisor case review required', en: 'Consultant Review Required', copy: 'Resources, carrier, or identity assumptions are uncertain and need advisor scoping before a firm route.' }
  },
  vi: {
    iui_or_ivf: { title: 'Lộ trình IUI / IVF từ cơ bản đến nâng cao', en: 'IUI or IVF Candidate', copy: 'Giao tử và người mang thai tương đối đầy đủ; bác sĩ sẽ quyết định IUI hay IVF.' },
    ivf: { title: 'Lộ trình IVF cổ điển', en: 'Classical IVF', copy: 'Kích trứng, lấy trứng, thụ tinh, nuôi phôi và chuyển phôi cho gia đình cần kiểm soát cao hơn.' },
    icsi_ivf: { title: 'Lộ trình ICSI / IVF thụ tinh chính xác', en: 'ICSI IVF', copy: 'Khi yếu tố tinh trùng yếu hoặc chưa rõ, labo phôi nên đánh giá ICSI và tối ưu tinh trùng.' },
    ropa: { title: 'Lộ trình ROPA / IVF hai mẹ', en: 'Reciprocal IVF / ROPA', copy: 'Một người cung cấp trứng, một người mang thai, kết hợp tinh trùng hiến tặng và rà soát pháp lý.' },
    donor_egg_ivf: { title: 'Lộ trình IVF với trứng hiến tặng', en: 'Donor Egg IVF', copy: 'Tạo phôi bằng trứng hiến tặng; sàng lọc, ghép chọn, đồng ý pháp lý và chất lượng labo là trọng tâm.' },
    embryo_transfer: { title: 'Chuyển phôi / vận chuyển phôi đã có', en: 'Embryo Transfer / Transport', copy: 'Bắt đầu từ phôi đông lạnh; trọng tâm là hồ sơ phôi, PGT, nơi lưu trữ và khả năng vận chuyển.' },
    surrogacy_ivf: { title: 'Lộ trình IVF mang thai hộ và quyền cha mẹ', en: 'Surrogacy IVF', copy: 'Cần kế hoạch giao tử, ghép người mang thai, ký quỹ, luật sư và xác lập quyền cha mẹ.' },
    needs_review: { title: 'Cần cố vấn đánh giá riêng', en: 'Consultant Review Required', copy: 'Nguồn lực, người mang thai hoặc thân phận còn chưa rõ; cần cố vấn làm rõ trước khi chốt lộ trình.' }
  }
};

export const eggSourceLabelsByLocale: Record<JourneyLocale, Record<EggSource, string>> = {
  'zh-Hans': { self: '自体卵子', partner: '伴侣卵子', donor: '第三方供卵', existing_embryo: '既有胚胎', unknown: '尚未确认' },
  en: { self: 'Self eggs', partner: 'Partner eggs', donor: 'Donor eggs', existing_embryo: 'Existing embryos', unknown: 'Not confirmed' },
  vi: { self: 'Trứng tự thân', partner: 'Trứng của bạn đời', donor: 'Trứng hiến tặng', existing_embryo: 'Phôi đã có', unknown: 'Chưa xác nhận' }
};

export const spermSourceLabelsByLocale: Record<JourneyLocale, Record<SpermSource, string>> = {
  'zh-Hans': { self: '自体精子', partner: '伴侣精子', donor: '第三方捐精', unknown: '尚未确认' },
  en: { self: 'Self sperm', partner: 'Partner sperm', donor: 'Donor sperm', unknown: 'Not confirmed' },
  vi: { self: 'Tinh trùng tự thân', partner: 'Tinh trùng của bạn đời', donor: 'Tinh trùng hiến tặng', unknown: 'Chưa xác nhận' }
};

export const carrierLabelsByLocale: Record<JourneyLocale, Record<CarrierType, string>> = {
  'zh-Hans': { self: '自体怀孕', partner: '伴侣怀孕', surrogate: '孕母承载', unknown: '尚未确认' },
  en: { self: 'Self gestation', partner: 'Partner gestation', surrogate: 'Gestational carrier', unknown: 'Not confirmed' },
  vi: { self: 'Tự mang thai', partner: 'Bạn đời mang thai', surrogate: 'Người mang thai hộ', unknown: 'Chưa xác nhận' }
};

export const countryStatusLabelsByLocale: Record<JourneyLocale, Record<GuidanceReport['countryFits'][number]['status'], string>> = {
  'zh-Hans': { ideal: '可比较', allowed: '可比较', restricted: '条件待确认', needs_review: '资料待复核', not_recommended: '当前不匹配' },
  en: { ideal: 'Comparable', allowed: 'Comparable', restricted: 'Conditions to confirm', needs_review: 'Details to verify', not_recommended: 'Not a current match' },
  vi: { ideal: 'Có thể so sánh', allowed: 'Có thể so sánh', restricted: 'Cần xác nhận điều kiện', needs_review: 'Cần rà soát dữ liệu', not_recommended: 'Hiện chưa phù hợp' }
};

export const validationCopyByLocale: Record<JourneyLocale, Record<string, string>> = {
  'zh-Hans': {
    single_male_requires_surrogacy_or_review: '单身男性通常需要孕母承载；若不使用代孕，需先由顾问个案评估。',
    male_couple_requires_surrogacy_or_review: '双男伴侣通常需要孕母承载与供卵安排，需由律师确认亲权路径。',
    male_identity_cannot_self_provide_eggs: '男性身份设定不应选择自体或伴侣卵子，除非存在跨性别或特殊医疗背景。',
    female_couple_requires_sperm_source_review: '双女伴侣需确认捐精或其他精子来源，否则无法形成可执行实验室方案。',
    self_carrier_requires_uterus_review: '亲自或伴侣怀孕需要子宫与内膜条件评估。',
    existing_embryo_requires_embryo_data: '既有胚胎路径需补充胚胎数量、PGT 状态、冷冻地点与转运可行性。',
    sex_selection_requires_country_legal_filter: '性别或胎数偏好必须通过目的地法律与医疗伦理审核。',
    twin_preference_requires_medical_review: '双胎或龙凤胎愿景需医师先评估多胎妊娠风险；报告仅能作初步评估。',
    existing_embryo_goal_requires_embryo_source: '已选择既有胚胎移植愿景，生育资源需同步设定为已有胚胎。',
    unknown_goal_requires_consultant_scoping: '宝宝愿景尚未确定，建议先由顾问界定家庭目标、可行国家与预算范围。',
    other_lgbtq_requires_consultant_review: '其他或未分类多元家庭情境需要顾问先厘清身份、配子与承载配置。',
    unknown_resource_requires_review: '目前仍有配子或承载来源未确认，报告只能作为初步咨询入口。'
  },
  en: {
    single_male_requires_surrogacy_or_review: 'Single intending fathers usually need a gestational carrier; without surrogacy, advisor review is required.',
    male_couple_requires_surrogacy_or_review: 'Male couples usually require donor eggs and a gestational carrier; parentage must be confirmed by counsel.',
    male_identity_cannot_self_provide_eggs: 'A male identity profile should not select self or partner eggs unless there is a transgender or special medical context.',
    female_couple_requires_sperm_source_review: 'Female couples must confirm donor sperm or another sperm source before the lab route is executable.',
    self_carrier_requires_uterus_review: 'Self or partner gestation requires uterus and endometrium assessment.',
    existing_embryo_requires_embryo_data: 'Existing embryo routes need embryo count, PGT status, storage location, and transport feasibility.',
    sex_selection_requires_country_legal_filter: 'Sex or twin preferences must pass destination law and medical ethics review.',
    twin_preference_requires_medical_review: 'Twin preferences require physician review of multiple pregnancy risk; the report remains preliminary.',
    existing_embryo_goal_requires_embryo_source: 'Existing embryo transfer was selected, so the resource setup should also use existing embryos.',
    unknown_goal_requires_consultant_scoping: 'The baby goal is not yet defined; an advisor should scope family goals, country fit, and budget.',
    other_lgbtq_requires_consultant_review: 'Other or uncategorized diverse family situations require advisor scoping of identity, gametes, and carrier.',
    unknown_resource_requires_review: 'Gamete or carrier sources are still unknown; this report can only serve as a preliminary intake.'
  },
  vi: {
    single_male_requires_surrogacy_or_review: 'Nam độc thân thường cần người mang thai hộ; nếu không dùng mang thai hộ, cần cố vấn đánh giá riêng.',
    male_couple_requires_surrogacy_or_review: 'Cặp đôi nam thường cần trứng hiến tặng và người mang thai hộ; luật sư phải xác nhận quyền cha mẹ.',
    male_identity_cannot_self_provide_eggs: 'Hồ sơ nam không nên chọn trứng tự thân hoặc trứng bạn đời, trừ bối cảnh chuyển giới hoặc y khoa đặc biệt.',
    female_couple_requires_sperm_source_review: 'Cặp đôi nữ cần xác nhận tinh trùng hiến tặng hoặc nguồn tinh trùng khác trước khi có lộ trình labo.',
    self_carrier_requires_uterus_review: 'Tự mang thai hoặc bạn đời mang thai cần đánh giá tử cung và niêm mạc.',
    existing_embryo_requires_embryo_data: 'Lộ trình phôi đã có cần số lượng phôi, tình trạng PGT, nơi lưu trữ và khả năng vận chuyển.',
    sex_selection_requires_country_legal_filter: 'Mong muốn giới tính hoặc song thai phải qua luật điểm đến và đạo đức y khoa.',
    twin_preference_requires_medical_review: 'Mong muốn song thai cần bác sĩ đánh giá rủi ro đa thai; báo cáo chỉ là sơ bộ.',
    existing_embryo_goal_requires_embryo_source: 'Đã chọn chuyển phôi có sẵn, nên nguồn sinh sản cũng phải đặt là phôi đã có.',
    unknown_goal_requires_consultant_scoping: 'Mục tiêu em bé chưa rõ; cố vấn cần xác định mục tiêu gia đình, quốc gia phù hợp và ngân sách.',
    other_lgbtq_requires_consultant_review: 'Gia đình đa dạng chưa phân loại cần cố vấn làm rõ thân phận, giao tử và người mang thai.',
    unknown_resource_requires_review: 'Nguồn giao tử hoặc người mang thai vẫn chưa rõ; báo cáo chỉ là đầu vào tư vấn sơ bộ.'
  }
};

export const uiCopy = {
  'zh-Hans': {
    progressKicker: 'IVFTODAY • PRIVATE BLUEPRINT',
    progressAria: '流程进度',
    selected: '已选中 SELECTED',
    visionSet: '已记录 VISION SET',
    tailored: '量身提供之途径 • TAILORED PATHWAYS',
    preliminaryOnly: 'PRELIMINARY ONLY',
    noMajorLimit: '暂未发现重大限制。',
    intro: {
      seal: '现代父亲路径',
      micro: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
      titleBefore: '为男同性伴侣与单身男性，',
      titleAccent: '规划父亲身份',
      titleAfter: '的每一步。',
      subtitle: '从供卵、胚胎与孕母，到跨国亲权、预算与时间线，一份私密、清晰、可执行的初步路径。',
      imageAlt: '两位男性父亲与婴儿在现代家中相互依靠',
      captionMicro: 'FATHERHOOD, PLANNED WITH CARE',
      caption: '父亲身份没有单一模板，但每一步都可以被认真规划。',
      copy1: 'IVFToday 将供卵、精子与胚胎选择、孕母承载、法律准入和费用节点整理成一条清晰路径。',
      copy2: '所有结果均为初步信息整理，不替代医师、律师、诊所或主管机关的正式意见。',
      guides: [
        ['跨国费用与法律准入', 'Cost + legal access', '/zh-hans/ivf-surrogacy-cost-legal-guide'],
        ['男同性伴侣与单身父亲', 'Country fit guide', '/zh-hans/lgbtq-single-parent-ivf-country-guide'],
        ['PGT、性别与双胎风险', 'Preliminary review', '/zh-hans/pgt-sex-selection-twin-risk-guide']
      ],
      pillars: [
        ['生理指标前导评析', 'Clinical Biomarker Assessment', 'AMH / 卵巢储备诊断'],
        ['跨国法权确令屏护', 'Transnational Legal Security', '亲权与准入核对'],
        ['财务资产安全信托', 'Financial Trust & Escrow', '通透费用分配']
      ],
      start: '开始父亲路径评估',
      startSmall: 'START FATHERHOOD ASSESSMENT',
      footer: 'PRIVATE FATHERHOOD PLANNING • PRELIMINARY ASSESSMENT'
    },
    goal: {
      overline: '步骤 02 / 05 • 家庭愿景 | FAMILY GOAL',
      title: '先确认您的宝宝愿景',
      en: 'Define the Desired Family Outcome',
      copy: '本步只收集目标偏好，不直接承诺胚胎性别、双胎结果、临床成功率或法律可行性。后续会由法律、医疗与资源条件逐层过滤。',
      disclaimer: '性别偏好、龙凤胎或双胎愿景会进入法律与医疗安全检查，不会在报告中作承诺性表述。',
      back: '返回父亲类型 • BACK TO PROFILE',
      next: '继续确认生育资源 • GO TO RESOURCES'
    },
    category: {
      overline: '步骤 01 / 05 • 父亲类型 | FATHERHOOD PROFILE',
      title: '你的父亲路径，从这里开始',
      en: 'Choose Your Fatherhood Profile',
      copy: '男同性伴侣与单身男性面对的精子选择、供卵、孕母和亲权文件并不相同。先选择与你相符的起点，后续只显示相关问题。',
      back: '返回首页 • BACK TO HOME',
      next: '继续确认家庭愿景 • GO TO FAMILY GOAL'
    },
    paths: {
      overline: '步骤 03 • 生育资源确认 | STEP 03 • RESOURCE CONFIRMATION',
      title: '厘清精子、供卵、胚胎与承载方式',
      en: 'Define Reproductive Resources Before Treatment',
      copy: '不再平行勾选治疗项目。系统会依照卵子来源、精子来源、妊娠承载方式与法律偏好，推导可行的医疗路径。',
      eggTitle: '卵子来源如何设定？',
      spermTitle: '精子来源如何设定？',
      carrierTitle: '妊娠承载方式如何设定？',
      embryoTitle: '既有胚胎目前状态？',
      derived: 'SYSTEM DERIVED PATHWAY',
      validation: 'VALIDATION / 冲突检查',
      back: '返回家庭愿景 • BACK TO FAMILY GOAL',
      next: '进入可行性与风险检查 • GO TO REVIEW'
    },
    review: {
      overline: '步骤 04 • 可行性与风险检查 | STEP 04 • FEASIBILITY REVIEW',
      title: '先做限制检查，再进医疗表单',
      en: 'Preliminary Gate Before Clinical Inputs',
      copy: '此处不直接生成承诺性方案，而是先依照身份、宝宝愿景、配子、承载方式与法律限制，标记需顾问或医师复核的部分。',
      currentStatus: 'CURRENT STATUS',
      needsReview: 'needsReview / 初步评估',
      ready: 'ready / 可进入动态表单',
      needsReviewCopy: '后续报告将使用 preliminary assessment wording，不作可行性承诺。',
      readyCopy: '目前没有重大路径冲突，仍需补齐医疗资料。',
      legalTitle: '法律准入检查',
      medicalTitle: '医疗安全检查',
      conflictTitle: '方案冲突检查',
      back: '返回生育资源 • BACK TO RESOURCES',
      next: '继续填写动态表单 • GO TO MEDICAL INPUTS'
    },
    medical: {
      overline: '步骤 05 • 生理指标 / 动态表单 | STEP 05 • DYNAMIC MEDICAL INPUTS',
      title: '建立您的生理健康档案',
      en: 'Formulate Your Clinical Biomarkers',
      copy: '本页依照上一页的配子与承载设定自动显示必要栏位。第三方供卵、捐精、孕母承载或既有胚胎路径，不再要求不相关的自体检查。',
      eggSection: '卵巢功能储备核心指标（自体 / 伴侣卵子）',
      eggBand: '卵子来源需医疗评估',
      eggBandCopy: '这些栏位只在使用自体或伴侣卵子时出现，用于预估促排反应、胚胎库策略与备案需求。',
      donorEggTitle: '第三方供卵路径',
      donorEggCopy: '已隐藏 AMH / FSH / AFC。报告将改以供卵者筛查、年龄、基因资料、法律同意与配型透明度作为核心风险。',
      embryoSection: '既有胚胎资料',
      embryoLocationPlaceholder: '例如：洛杉矶诊所 / 台北实验室',
      embryoTransport: '已有出库 / 转运文件初步确认',
      embryoNoticeTitle: '胚胎转运提醒',
      embryoNoticeCopy: '既有胚胎路径需要补齐 Day 5/Day 6、冷冻地点、实验室出库规则、跨境转运文件与解冻风险。',
      spermTitle: '精子质量与活跃度分析评级',
      donorSpermTitle: '第三方捐精路径',
      donorSpermCopy: '已隐藏精液活力栏位。报告将改以精子库资质、基因筛查、样本运输与亲权同意文件作为核心风险。',
      carrierTitle: '子宫与妊娠承载条件',
      endometrium: '内膜状态 / Endometrium readiness',
      miscarriage: '曾有反复流产或妊娠中止史',
      failedTransfer: '曾有胚胎移植失败史',
      surrogateTitle: '孕母承载路径',
      surrogateCopy: '已隐藏自身子宫承载评估。报告将把重点转向孕母匹配、医疗保险、信托托管、心理评估与亲权法律文件。',
      notes: '其他生理学备忘、既往手术病史或健康备注（选填）',
      notesPlaceholder: '例如：具有子宫内膜异位症、多囊卵巢、甲状腺功能失调，或者之前试管婴儿（IVF）培育囊胚的比率资料等。',
      back: '返回可行性检查 • BACK TO REVIEW',
      next: '生成初步规划分析报告 • RUN REPORT'
    },
    report: {
      overline: '您的专属生育蓝图精算报告（PARENTHOOD BLUEPRINT）',
      reset: '重置当前配置',
      familyGoal: '家庭愿景',
      identity: '身份类别',
      recommended: '推荐路径',
      carrier: '承载方式',
      egg: '卵子来源',
      sperm: '精子来源',
      tabs: {
        legality: ['法律准入', 'legality & access'],
        pathway: ['推荐路径', 'recommended pathway'],
        medical: ['医疗可行性', 'medical readiness'],
        cost: ['财务精算', 'capital & escrow'],
        risk: ['风险提示', 'risk & missing data'],
        timeline: ['实践日程', 'milestones timeline'],
        register: ['留资预约', 'private registration']
      } as Record<ReportTab, [string, string]>
    }
  },
  en: {
    progressKicker: 'IVFTODAY • PRIVATE BLUEPRINT',
    progressAria: 'Planning progress',
    selected: 'SELECTED',
    visionSet: 'VISION SET',
    tailored: 'TAILORED PATHWAYS',
    preliminaryOnly: 'PRELIMINARY ONLY',
    noMajorLimit: 'No major limitation found yet.',
    intro: {
      seal: 'MODERN FATHERHOOD',
      micro: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
      titleBefore: 'A stronger route to ',
      titleAccent: 'fatherhood',
      titleAfter: ', built around you.',
      subtitle: 'Plan donor eggs, embryos, a gestational carrier, cross-border parentage, budget, and timing in one private preliminary pathway.',
      imageAlt: 'Two fathers with their baby in a modern home',
      captionMicro: 'FATHERHOOD, PLANNED WITH CARE',
      caption: 'Fatherhood is not one family template. Every step can be planned with care.',
      copy1: 'IVFToday organizes donor eggs, sperm and embryo decisions, carrier arrangements, legal access, and cost milestones into one clear route.',
      copy2: 'All outputs are preliminary information and do not replace a physician, attorney, clinic, or competent authority.',
      guides: [
        ['Cost and legal access', 'Cross-border guide', '/en/ivf-surrogacy-cost-legal-guide'],
        ['Gay men and single fathers', 'Country fit guide', '/en/gay-men-single-father-ivf-country-guide'],
        ['PGT, sex preference, twins', 'Risk review', '/en/pgt-sex-selection-twin-risk-guide']
      ],
      pillars: [
        ['Clinical biomarker assessment', 'AMH / ovarian reserve', 'Resource-based dynamic inputs'],
        ['Transnational legal security', 'Parentage and access review', 'Identity, donor, and carrier screening'],
        ['Financial trust and escrow', 'Transparent budget allocation', 'Country and pathway cost bands']
      ],
      start: 'Start fatherhood assessment',
      startSmall: 'START FATHERHOOD ASSESSMENT',
      footer: 'PRIVATE FATHERHOOD PLANNING • PRELIMINARY ASSESSMENT'
    },
    goal: {
      overline: 'STEP 02 / 05 • FAMILY GOAL',
      title: 'Confirm the family goal first',
      en: 'Define the Desired Family Outcome',
      copy: 'This step records the goal only. It does not promise embryo sex, twins, clinical success, or legal feasibility. Later steps filter the goal through law, medicine, and resources.',
      disclaimer: 'Sex preferences, boy-girl twins, or twin goals are sent into legal and medical safety review and are never written as guaranteed outcomes.',
      back: 'BACK TO FATHERHOOD PROFILE',
      next: 'GO TO RESOURCES'
    },
    category: {
      overline: 'STEP 01 / 05 • FATHERHOOD PROFILE',
      title: 'Your fatherhood route starts here',
      en: 'Choose Your Fatherhood Profile',
      copy: 'Male couples and single intended fathers face different sperm-source decisions, donor, carrier, and parentage requirements. Choose the starting point that fits you.',
      back: 'BACK TO HOME',
      next: 'GO TO FAMILY GOAL'
    },
    paths: {
      overline: 'STEP 03 • RESOURCE CONFIRMATION',
      title: 'Clarify gametes, carrier, and family preferences',
      en: 'Define Reproductive Resources Before Treatment',
      copy: 'Instead of choosing treatment labels in parallel, the system derives the likely route from egg source, sperm source, carrier, and legal preferences.',
      eggTitle: 'How should the egg source be set?',
      spermTitle: 'How should the sperm source be set?',
      carrierTitle: 'How should the gestational carrier be set?',
      embryoTitle: 'What is the status of existing embryos?',
      derived: 'SYSTEM DERIVED PATHWAY',
      validation: 'VALIDATION / CONFLICT CHECK',
      back: 'BACK TO FAMILY GOAL',
      next: 'GO TO REVIEW'
    },
    review: {
      overline: 'STEP 04 • FEASIBILITY REVIEW',
      title: 'Check limits before medical inputs',
      en: 'Preliminary Gate Before Clinical Inputs',
      copy: 'This page does not generate promises. It flags items that need advisor, physician, or attorney review based on identity, baby goal, gametes, carrier, and legal limits.',
      currentStatus: 'CURRENT STATUS',
      needsReview: 'needsReview / preliminary assessment',
      ready: 'ready / dynamic form available',
      needsReviewCopy: 'The report will use preliminary wording and will not make feasibility promises.',
      readyCopy: 'No major route conflict is detected, but medical data still needs completion.',
      legalTitle: 'Legal access check',
      medicalTitle: 'Medical safety check',
      conflictTitle: 'Plan conflict check',
      back: 'BACK TO RESOURCES',
      next: 'GO TO MEDICAL INPUTS'
    },
    medical: {
      overline: 'STEP 05 • DYNAMIC MEDICAL INPUTS',
      title: 'Build the clinical health file',
      en: 'Formulate Your Clinical Biomarkers',
      copy: 'The form only shows fields that match the resource setup. Donor eggs, donor sperm, gestational carrier, or existing embryo routes hide unrelated self-testing fields.',
      eggSection: 'Ovarian reserve indicators (self / partner eggs)',
      eggBand: 'Egg source requires medical assessment',
      eggBandCopy: 'These fields only appear for self or partner eggs and support stimulation response, embryo banking, and backup planning.',
      donorEggTitle: 'Donor egg pathway',
      donorEggCopy: 'AMH / FSH / AFC are hidden. The report focuses on donor screening, age, genetics, legal consent, and matching transparency.',
      embryoSection: 'Existing embryo data',
      embryoLocationPlaceholder: 'Example: Los Angeles clinic / Bangkok lab',
      embryoTransport: 'Initial release / transport documents confirmed',
      embryoNoticeTitle: 'Embryo transport reminder',
      embryoNoticeCopy: 'Existing embryo routes need Day 5/Day 6 details, storage location, lab release rules, transport documents, and thaw risk review.',
      spermTitle: 'Sperm quality and motility classification',
      donorSpermTitle: 'Donor sperm pathway',
      donorSpermCopy: 'Semen fields are hidden. The report focuses on sperm bank credentials, genetic screening, sample transport, and parentage consent.',
      carrierTitle: 'Uterus and carrier readiness',
      endometrium: 'Endometrium readiness',
      miscarriage: 'Prior recurrent miscarriage or pregnancy loss',
      failedTransfer: 'Prior failed embryo transfer',
      surrogateTitle: 'Gestational carrier pathway',
      surrogateCopy: 'Self uterus assessment is hidden. The report focuses on carrier matching, insurance, escrow, psychological screening, and parentage documents.',
      notes: 'Additional clinical history, prior procedures, or health notes (optional)',
      notesPlaceholder: 'Example: endometriosis, PCOS, thyroid dysfunction, prior IVF blastocyst rates, or transfer history.',
      back: 'BACK TO REVIEW',
      next: 'RUN PRELIMINARY REPORT'
    },
    report: {
      overline: 'YOUR PRIVATE PARENTHOOD BLUEPRINT',
      reset: 'Reset current configuration',
      familyGoal: 'Family goal',
      identity: 'Identity',
      recommended: 'Recommended path',
      carrier: 'Carrier',
      egg: 'Egg source',
      sperm: 'Sperm source',
      tabs: {
        legality: ['Legal access', 'legality & access'],
        pathway: ['Recommended path', 'recommended pathway'],
        medical: ['Medical readiness', 'medical readiness'],
        cost: ['Cost plan', 'capital & escrow'],
        risk: ['Risk review', 'risk & missing data'],
        timeline: ['Timeline', 'milestones timeline'],
        register: ['Private intake', 'private registration']
      } as Record<ReportTab, [string, string]>
    }
  },
  vi: {
    progressKicker: 'IVFTODAY • PRIVATE BLUEPRINT',
    progressAria: 'Tiến độ lập kế hoạch',
    selected: 'ĐÃ CHỌN',
    visionSet: 'ĐÃ GHI NHẬN',
    tailored: 'LỘ TRÌNH CÁ NHÂN HÓA',
    preliminaryOnly: 'CHỈ LÀ ĐÁNH GIÁ SƠ BỘ',
    noMajorLimit: 'Chưa phát hiện hạn chế lớn.',
    intro: {
      seal: 'LỘ TRÌNH LÀM CHA HIỆN ĐẠI',
      micro: 'FOR GAY MEN + SINGLE INTENDED FATHERS',
      titleBefore: 'Một lộ trình vững vàng hơn để ',
      titleAccent: 'trở thành cha',
      titleAfter: '.',
      subtitle: 'Lập kế hoạch trứng hiến, phôi, người mang thai hộ, quyền làm cha xuyên biên giới, ngân sách và thời gian trong một lộ trình riêng tư.',
      imageAlt: 'Hai người cha và em bé trong một ngôi nhà hiện đại',
      captionMicro: 'FATHERHOOD, PLANNED WITH CARE',
      caption: 'Làm cha không chỉ có một khuôn mẫu gia đình. Mỗi bước đều có thể được chuẩn bị kỹ lưỡng.',
      copy1: 'IVFToday sắp xếp nguồn trứng, tinh trùng, phôi, người mang thai, pháp lý và chi phí thành một lộ trình rõ ràng.',
      copy2: 'Mọi kết quả chỉ là thông tin sơ bộ và không thay thế bác sĩ, luật sư, phòng khám hoặc cơ quan có thẩm quyền.',
      guides: [
        ['Chi phí và pháp lý quốc tế', 'Cross-border guide', '/vi/ivf-surrogacy-cost-legal-guide'],
        ['Cặp đôi nam và cha đơn thân', 'Country fit guide', '/vi/lgbtq-single-parent-ivf-country-guide'],
        ['PGT, giới tính, song thai', 'Risk review', '/vi/pgt-sex-selection-twin-risk-guide']
      ],
      pillars: [
        ['Đánh giá chỉ số y khoa', 'AMH / dự trữ buồng trứng', 'Biểu mẫu động theo nguồn lực'],
        ['An toàn pháp lý xuyên biên giới', 'Quyền cha mẹ và điều kiện tiếp cận', 'Rà soát thân phận, hiến tặng và người mang thai'],
        ['Kế hoạch tài chính và ký quỹ', 'Phân bổ ngân sách minh bạch', 'Dải chi phí theo quốc gia và lộ trình']
      ],
      start: 'Bắt đầu đánh giá lộ trình làm cha',
      startSmall: 'START FATHERHOOD ASSESSMENT',
      footer: 'PRIVATE FATHERHOOD PLANNING • PRELIMINARY ASSESSMENT'
    },
    goal: {
      overline: 'BƯỚC 02 / 05 • MỤC TIÊU GIA ĐÌNH',
      title: 'Xác nhận mong muốn về em bé trước',
      en: 'Define the Desired Family Outcome',
      copy: 'Bước này chỉ ghi nhận mục tiêu. Không cam kết giới tính phôi, song thai, tỷ lệ thành công hoặc khả thi pháp lý. Các bước sau sẽ lọc qua pháp lý, y khoa và nguồn lực.',
      disclaimer: 'Mong muốn giới tính, sinh đôi trai gái hoặc song thai sẽ đi vào kiểm tra pháp lý và an toàn y khoa, không được viết như cam kết.',
      back: 'QUAY LẠI HỒ SƠ LÀM CHA',
      next: 'ĐẾN NGUỒN SINH SẢN'
    },
    category: {
      overline: 'BƯỚC 01 / 05 • HỒ SƠ LÀM CHA',
      title: 'Lộ trình làm cha bắt đầu tại đây',
      en: 'Choose Your Fatherhood Profile',
      copy: 'Cặp đôi nam và nam độc thân có nhu cầu khác nhau về nguồn tinh trùng, trứng hiến, người mang thai và quyền làm cha. Hãy chọn điểm bắt đầu phù hợp.',
      back: 'QUAY LẠI TRANG CHỦ',
      next: 'ĐẾN MỤC TIÊU GIA ĐÌNH'
    },
    paths: {
      overline: 'BƯỚC 03 • XÁC NHẬN NGUỒN SINH SẢN',
      title: 'Làm rõ nguồn giao tử, người mang thai và mong muốn gia đình',
      en: 'Define Reproductive Resources Before Treatment',
      copy: 'Không chọn song song nhiều nhãn điều trị. Hệ thống suy ra lộ trình từ nguồn trứng, nguồn tinh trùng, người mang thai và ưu tiên pháp lý.',
      eggTitle: 'Nguồn trứng được thiết lập thế nào?',
      spermTitle: 'Nguồn tinh trùng được thiết lập thế nào?',
      carrierTitle: 'Người mang thai được thiết lập thế nào?',
      embryoTitle: 'Tình trạng phôi đã có là gì?',
      derived: 'SYSTEM DERIVED PATHWAY',
      validation: 'VALIDATION / KIỂM TRA XUNG ĐỘT',
      back: 'QUAY LẠI MỤC TIÊU GIA ĐÌNH',
      next: 'ĐẾN KIỂM TRA KHẢ THI'
    },
    review: {
      overline: 'BƯỚC 04 • KIỂM TRA KHẢ THI',
      title: 'Kiểm tra hạn chế trước khi nhập chỉ số y khoa',
      en: 'Preliminary Gate Before Clinical Inputs',
      copy: 'Trang này không tạo cam kết. Hệ thống đánh dấu mục cần cố vấn, bác sĩ hoặc luật sư rà soát dựa trên thân phận, mục tiêu em bé, giao tử, người mang thai và pháp lý.',
      currentStatus: 'CURRENT STATUS',
      needsReview: 'needsReview / đánh giá sơ bộ',
      ready: 'ready / có thể nhập biểu mẫu động',
      needsReviewCopy: 'Báo cáo sẽ dùng ngôn ngữ sơ bộ và không cam kết khả thi.',
      readyCopy: 'Chưa thấy xung đột lớn, nhưng vẫn cần hoàn thiện dữ liệu y khoa.',
      legalTitle: 'Kiểm tra pháp lý tiếp cận',
      medicalTitle: 'Kiểm tra an toàn y khoa',
      conflictTitle: 'Kiểm tra xung đột phương án',
      back: 'QUAY LẠI NGUỒN SINH SẢN',
      next: 'ĐẾN CHỈ SỐ Y KHOA'
    },
    medical: {
      overline: 'BƯỚC 05 • BIỂU MẪU Y KHOA ĐỘNG',
      title: 'Xây dựng hồ sơ sức khỏe sinh sản',
      en: 'Formulate Your Clinical Biomarkers',
      copy: 'Biểu mẫu chỉ hiện các trường phù hợp với nguồn lực đã chọn. Trứng hiến tặng, tinh trùng hiến tặng, mang thai hộ hoặc phôi đã có sẽ ẩn trường không liên quan.',
      eggSection: 'Chỉ số dự trữ buồng trứng (trứng tự thân / bạn đời)',
      eggBand: 'Nguồn trứng cần đánh giá y khoa',
      eggBandCopy: 'Các trường này chỉ xuất hiện khi dùng trứng tự thân hoặc bạn đời, giúp dự đoán đáp ứng kích trứng, tích lũy phôi và phương án dự phòng.',
      donorEggTitle: 'Lộ trình trứng hiến tặng',
      donorEggCopy: 'AMH / FSH / AFC được ẩn. Báo cáo tập trung vào sàng lọc người hiến, tuổi, gene, đồng ý pháp lý và tính minh bạch ghép chọn.',
      embryoSection: 'Dữ liệu phôi đã có',
      embryoLocationPlaceholder: 'Ví dụ: phòng khám Los Angeles / labo Bangkok',
      embryoTransport: 'Đã xác nhận sơ bộ giấy tờ xuất kho / vận chuyển',
      embryoNoticeTitle: 'Nhắc về vận chuyển phôi',
      embryoNoticeCopy: 'Lộ trình phôi đã có cần thông tin Day 5/Day 6, nơi lưu trữ, quy định xuất kho, giấy tờ vận chuyển và rủi ro rã đông.',
      spermTitle: 'Phân loại chất lượng và độ di động tinh trùng',
      donorSpermTitle: 'Lộ trình tinh trùng hiến tặng',
      donorSpermCopy: 'Trường tinh dịch được ẩn. Báo cáo tập trung vào ngân hàng tinh trùng, sàng lọc gene, vận chuyển mẫu và đồng ý quyền cha mẹ.',
      carrierTitle: 'Tử cung và khả năng mang thai',
      endometrium: 'Sẵn sàng niêm mạc tử cung',
      miscarriage: 'Từng sảy thai tái diễn hoặc mất thai',
      failedTransfer: 'Từng chuyển phôi thất bại',
      surrogateTitle: 'Lộ trình người mang thai hộ',
      surrogateCopy: 'Đánh giá tử cung của khách hàng được ẩn. Báo cáo tập trung vào ghép người mang thai, bảo hiểm, ký quỹ, tâm lý và giấy tờ quyền cha mẹ.',
      notes: 'Tiền sử y khoa, phẫu thuật hoặc ghi chú sức khỏe khác (tùy chọn)',
      notesPlaceholder: 'Ví dụ: lạc nội mạc tử cung, PCOS, rối loạn tuyến giáp, tỷ lệ phôi nang IVF trước đây hoặc lịch sử chuyển phôi.',
      back: 'QUAY LẠI KIỂM TRA KHẢ THI',
      next: 'TẠO BÁO CÁO SƠ BỘ'
    },
    report: {
      overline: 'BÁO CÁO KẾ HOẠCH SINH SẢN RIÊNG',
      reset: 'Đặt lại cấu hình',
      familyGoal: 'Mục tiêu gia đình',
      identity: 'Nhóm khách hàng',
      recommended: 'Lộ trình đề xuất',
      carrier: 'Người mang thai',
      egg: 'Nguồn trứng',
      sperm: 'Nguồn tinh trùng',
      tabs: {
        legality: ['Pháp lý', 'legality & access'],
        pathway: ['Lộ trình đề xuất', 'recommended pathway'],
        medical: ['Khả thi y khoa', 'medical readiness'],
        cost: ['Chi phí', 'capital & escrow'],
        risk: ['Rủi ro', 'risk & missing data'],
        timeline: ['Lịch trình', 'milestones timeline'],
        register: ['Đăng ký riêng', 'private registration']
      } as Record<ReportTab, [string, string]>
    }
  }
};
